import React, { useState, useRef, useEffect, useMemo } from "react";
import { IoChevronDown, IoAdd, IoTrash } from "react-icons/io5";
import { usePortfolioData } from "contexts/PortfolioContext";
import TooltipHorizon from "components/tooltip";
import GroupCreator from "pages/overview/groups/GroupCreator";
import { getCurrentUser } from "aws-amplify/auth";

const PortfolioNameSelector = ({
    portfolioNames = [],
    value,
    onChange,
    size,
    isPortfolioLoading,
    currentGroup,
    username,
}) => {
    const {
        currentPortfolioName,
        portfolioFacilityGroups,
        deletePortfolioGroup,
        setCurrentGroup
    } = usePortfolioData();
    const [showGroupCreator, setShowGroupCreator] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const modalRef = useRef(null);
    const optionsRef = useRef([]);
    const [displayValue, setDisplayValue] = useState("Select Portfolio");

    // Safely wrap portfolio names to ensure it's always an array
    const safePortfolioNames = Array.isArray(portfolioNames) ? portfolioNames : [];

    // Group the portfolio groups by portfolioId
    const groupsByPortfolio = portfolioFacilityGroups ? portfolioFacilityGroups.reduce((acc, group) => {
        if (!acc[group.portfolioId]) {
            acc[group.portfolioId] = [];
        }
        acc[group.portfolioId].push(group);
        return acc;
    }, {}) : {};

    // Prepare flattened options list for keyboard navigation
    const flattenedOptions = useMemo(() => [
        { id: "placeholder", value: "", label: "Select Portfolio", isGroup: false, groupId: null },
        ...safePortfolioNames.flatMap(portfolioName => [
            { id: portfolioName, value: portfolioName, label: portfolioName.charAt(0).toUpperCase() + portfolioName.slice(1).toLowerCase(), isGroup: false, groupId: null },
            ...(groupsByPortfolio[portfolioName] || []).map(group => ({
                id: group.id,
                value: group.id,
                label: group.name,
                isGroup: true,
                groupId: group.id,
                parentPortfolio: portfolioName
            }))
        ])
    ], [safePortfolioNames, groupsByPortfolio]);

    // Update display value when value changes
    useEffect(() => {
        if (currentGroup) {
            // If we have a current group, always use that for display
            setDisplayValue(`${currentGroup.name} (Group)`);
            return;
        }

        // Handle empty value (including empty string)
        if (!value || value === "") {
            setDisplayValue("Select Portfolio");
            return;
        }

        // Check if value indicates a group
        if (value.startsWith('portfolio-group-')) {
            // It's a group - find its name
            const group = portfolioFacilityGroups.find(g => g.id === value);
            if (group) {
                setDisplayValue(`${group.name} (Group)`);
            } else {
                setDisplayValue(value);
            }
        } else {
            // It's a regular portfolio
            setDisplayValue(value);
        }
    }, [value, portfolioFacilityGroups, currentGroup]);

    // Click outside handler for both dropdown and modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
            if (showGroupCreator && modalRef.current && !modalRef.current.contains(event.target)) {
                setShowGroupCreator(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showGroupCreator]);

    // Update refs array when options change
    useEffect(() => {
        optionsRef.current = optionsRef.current.slice(0, flattenedOptions.length);
    }, [flattenedOptions]);

    // Keyboard event handler
    const handleKeyDown = (e) => {
        // Don't handle keyboard events when modal is open
        if (showGroupCreator) return;

        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setActiveIndex(0);
            }
            return;
        }

        const totalOptions = flattenedOptions.length;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prevIndex =>
                    prevIndex < totalOptions - 1 ? prevIndex + 1 : 0
                );
                break;

            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prevIndex =>
                    prevIndex > 0 ? prevIndex - 1 : totalOptions - 1
                );
                break;

            case 'Enter':
            case ' ':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < totalOptions) {
                    const selectedOption = flattenedOptions[activeIndex];
                    if (selectedOption.id === "placeholder") {
                        handleSelect("", false, null, null);
                    } else if (selectedOption.isGroup) {
                        handleSelect(selectedOption.value, true, selectedOption.groupId, selectedOption.parentPortfolio);
                    } else {
                        handleSelect(selectedOption.value, false, null, null);
                    }
                }
                break;

            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;

            case 'Tab':
                setIsOpen(false);
                break;

            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;

            case 'End':
                e.preventDefault();
                setActiveIndex(totalOptions - 1);
                break;

            default:
                const key = e.key.toLowerCase();
                if (key.length === 1 && /[a-z0-9]/.test(key)) {
                    const nextIndex = flattenedOptions.findIndex((option, index) =>
                        index > activeIndex && option.label.toLowerCase().startsWith(key)
                    );

                    if (nextIndex !== -1) {
                        setActiveIndex(nextIndex);
                    } else {
                        const firstIndex = flattenedOptions.findIndex((option) =>
                            option.label.toLowerCase().startsWith(key)
                        );

                        if (firstIndex !== -1) {
                            setActiveIndex(firstIndex);
                        }
                    }
                }
                break;
        }
    };

    // Scroll active option into view
    useEffect(() => {
        if (isOpen && activeIndex >= 0 && optionsRef.current[activeIndex]) {
            optionsRef.current[activeIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [activeIndex, isOpen]);

    const handleSelect = (selectedValue, isGroup = false, groupId = null, parentPortfolio = null) => {
        setIsOpen(false);
        // Handle placeholder selection (clearing the selection)
        if (selectedValue === "") {
            setDisplayValue("Select Portfolio");
            if (onChange) {
                onChange("");
            }
            return;
        }

        if (isGroup) {
            // Find the group in portfolioFacilityGroups
            const selectedGroup = portfolioFacilityGroups.find(g => g.id === groupId);
            if (selectedGroup && onChange) {
                onChange(selectedValue, {
                    isGroup: true,
                    groupId: groupId,
                    portfolioId: parentPortfolio,
                    group: selectedGroup // Pass the full group object
                });
            }
        } else {
            if (onChange) {
                onChange(selectedValue);
            }
        }
    };

    return (
        <div
            className="relative w-full sm:min-w-[150px] lg:min-w-[200px] xl:min-w-[250px]"
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={isPortfolioLoading ? "-1" : "0"}
        >
            {/* Main selector with group actions */}
            <div className="flex items-center gap-1 w-full">
                <div
                    className={`flex w-full items-center justify-between px-3 py-2 bg-white border-b border-[#89949f] ${isPortfolioLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        } ${size === "small" ? "text-sm" : "text-base"} ${isOpen ? "border-blue-500" : ""
                        }`}
                    onClick={() => !isPortfolioLoading && setIsOpen(!isOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls="portfolio-name-selector"
                    role="combobox"
                    aria-disabled={isPortfolioLoading}
                >
                    <span className="truncate overflow-hidden">
                        {isPortfolioLoading ? "Loading..." : displayValue}
                    </span>
                    {!isPortfolioLoading && (
                        <IoChevronDown
                            className={`w-[12px] h-[12px] transition-transform ml-2 ${isOpen ? 'rotate-180' : ''
                                }`}
                        />
                    )}
                </div>

                {/* Group action buttons */}
                {currentPortfolioName && !isPortfolioLoading && (
                    <div className="flex items-center gap-1">
                        <TooltipHorizon
                            content="Create a new portfolio group"
                            placement="top"
                            extra="text-xs bg-opacity-50 text-gray-700 top-4"
                            trigger={
                                <button
                                    onClick={() => setShowGroupCreator(true)}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"
                                    aria-label="Create a new portfolio group"
                                >
                                    <IoAdd size={15} />
                                </button>
                            }
                        />
                        {/* Delete Group button - only show when a group is selected */}
                        {currentGroup && (
                            <TooltipHorizon
                                content={
                                    currentGroup.createdBy === username
                                        ? "Delete this portfolio group"
                                        : "Only the creator can delete this group"
                                }
                                placement="bottom"
                                extra="text-xs bg-opacity-50 text-gray-700 top-4"
                                trigger={
                                    <button
                                        onClick={async () => {
                                            console.log('Delete button clicked. Group createdBy:', currentGroup.createdBy, 'Current username:', username);
                                            if (currentGroup.createdBy === username &&
                                                window.confirm('Are you sure you want to delete this portfolio group?')) {
                                                const success = await deletePortfolioGroup(currentGroup.id);
                                                if (success) {
                                                    setCurrentGroup(null);
                                                }
                                            }
                                        }}
                                        className={`p-1 rounded-full ${currentGroup.createdBy === username
                                            ? 'text-red-600 hover:bg-red-50'
                                            : 'text-gray-400 cursor-not-allowed'
                                            }`}
                                        disabled={currentGroup.createdBy !== username}
                                        aria-label="Delete portfolio group"
                                    >
                                        <IoTrash size={15} />
                                    </button>
                                }
                            />
                        )}
                    </div>
                )}
            </div>

            {isOpen && !isPortfolioLoading && (
                <div
                    className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                    style={{ minWidth: '200px' }}
                    role="listbox"
                    id="portfolio-name-selector"
                >
                    <ul className="py-1">
                        {flattenedOptions.map((option, index) => (
                            <li
                                key={option.id}
                                ref={el => optionsRef.current[index] = el}
                                className={`${option.isGroup ? 'pl-8 pr-4 italic text-gray-700' : 'px-4 font-medium'} py-2 ${activeIndex === index ? "bg-blue-100" : "hover:bg-gray-100"
                                    } cursor-pointer ${option.id === "placeholder" ? "text-gray-500" : ""}`}
                                onClick={() => {
                                    if (option.id === "placeholder") {
                                        handleSelect("", false, null, null);
                                    } else if (option.isGroup) {
                                        handleSelect(option.value, true, option.groupId, option.parentPortfolio);
                                    } else {
                                        handleSelect(option.value, false, null, null);
                                    }
                                }}
                                onMouseEnter={() => setActiveIndex(index)}
                                role="option"
                                aria-selected={activeIndex === index}
                                tabIndex="-1"
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Group Creator Modal */}
            {showGroupCreator && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div ref={modalRef}>
                        <GroupCreator onClose={() => setShowGroupCreator(false)} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioNameSelector; 